/**
 * Agent access state for the tab (ADR-009 §1.1, §5.2): pairings, the
 * connection status, pending consent requests and the activity log.
 *
 * This module is in the initial bundle and deliberately small. It never
 * opens a network connection itself: the bridge runtime (BridgeClient +
 * AgentGateway, a separate chunk) is loaded, and a loopback socket opened,
 * only when
 * - the user clicks Allow on a pairing link (a `#agent-pair=` fragment), or
 * - the user clicks Connect in the Agent panel, or
 * - a pairing the user marked "Connect automatically" exists (a persisted,
 *   user-granted setting; its hint lives in localStorage so an ordinary
 *   visitor's page load does not even open IndexedDB).
 *
 * Pairing keys are non-extractable HMAC CryptoKeys in IndexedDB
 * `maipo-agent`; page script can use them to answer a challenge but cannot
 * read their bytes.
 */
import type { PairingGrants, PairingLink } from '@maipo/agent-protocol';
import type { ActivityEntry, ConsentDecision, ConsentRequest, GatewayHost } from './gateway';

export const AUTO_CONNECT_HINT = 'maipo-agent-autoconnect';
const DATABASE = 'maipo-agent';
const STORE = 'pairings';
const MAX_ACTIVITY = 200;

export interface StoredPairing {
  pairingId: string;
  key: CryptoKey;
  grants: PairingGrants;
  createdAt: string;
  lastConnectedAt?: string;
  autoConnect: boolean;
}
export type PairingSummary = Omit<StoredPairing, 'key'>;

export type ConnectionStatus =
  | { state: 'idle' }
  | { state: 'connecting'; pairingId: string; pairing: boolean }
  | { state: 'connected'; pairingId: string; since: string }
  | { state: 'disconnected'; pairingId?: string; reason: string }
  | { state: 'error'; pairingId?: string; reason: string; permission?: boolean };

export interface AgentAccessState {
  status: ConnectionStatus;
  pairings: PairingSummary[];
  /** A pairing link waiting for the user's Allow/Deny. */
  pendingLink: PairingLink | null;
  /** A link that could not be used (shown once). */
  linkError: string | null;
  consents: ConsentRequest[];
  activity: ActivityEntry[];
  fullPreviewSessions: ReadonlySet<string>;
  allowWithoutCheckpoint: boolean;
  loaded: boolean;
}

type Listener = () => void;
const listeners = new Set<Listener>();
let state: AgentAccessState = {
  status: { state: 'idle' },
  pairings: [],
  pendingLink: null,
  linkError: null,
  consents: [],
  activity: [],
  fullPreviewSessions: new Set(),
  allowWithoutCheckpoint: false,
  loaded: false,
};

export function agentState(): AgentAccessState { return state; }
export function subscribeAgentState(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
export function updateAgentState(patch: Partial<AgentAccessState>): void {
  state = { ...state, ...patch };
  for (const listener of [...listeners]) listener();
}

export function logActivity(entry: ActivityEntry): void {
  updateAgentState({ activity: [entry, ...state.activity].slice(0, MAX_ACTIVITY) });
}

// ---------------------------------------------------------------------------
// Consent queue. Decisions come only from trusted clicks (see AgentDialogs).

const consentWaiters = new Map<string, (decision: ConsentDecision) => void>();
/** At most this many requests wait for the user at once; more are refused, not stacked (review finding 7). */
export const MAX_PENDING_CONSENTS = 2;
const pendingByKey = new Map<string, { promise: Promise<ConsentDecision>; id: string }>();
const consentKey = (request: ConsentRequest) => JSON.stringify([request.kind, request.client, request.title, request.detail, request.documentName ?? '', request.destination ?? '']);

export function requestConsent(request: ConsentRequest, signal: AbortSignal): Promise<ConsentDecision> {
  // An identical request already on screen is answered once, for both.
  const key = consentKey(request);
  const duplicate = pendingByKey.get(key);
  if (duplicate) return duplicate.promise;
  if (state.consents.length >= MAX_PENDING_CONSENTS) return Promise.resolve('busy');
  const promise = new Promise<ConsentDecision>((resolve) => {
    const finish = (decision: ConsentDecision) => {
      if (!consentWaiters.has(request.id)) return;
      consentWaiters.delete(request.id);
      if (pendingByKey.get(key)?.id === request.id) pendingByKey.delete(key);
      updateAgentState({ consents: state.consents.filter((item) => item.id !== request.id) });
      resolve(decision);
    };
    consentWaiters.set(request.id, finish);
    updateAgentState({ consents: [...state.consents, request] });
    signal.addEventListener('abort', () => finish('deny'), { once: true });
  });
  pendingByKey.set(key, { promise, id: request.id });
  return promise;
}

/** Dialog buttons ignore clicks this soon after the dialog appears, so a click aimed elsewhere cannot land on Allow. */
export const CONSENT_CLICK_DELAY_MS = 400;
export function consentClickTooSoon(shownAt: number, now: number): boolean {
  return now - shownAt < CONSENT_CLICK_DELAY_MS;
}

/** Called by the dialog's buttons. `trusted` must be `event.isTrusted`: a scripted click never decides. */
export function answerConsent(id: string, decision: 'allow' | 'deny', trusted: boolean, options: { disconnect?: boolean } = {}): boolean {
  if (!trusted) return false;
  const waiter = consentWaiters.get(id);
  if (!waiter) return false;
  waiter(decision);
  if (options.disconnect) void disconnectBridge();
  return true;
}

// ---------------------------------------------------------------------------
// IndexedDB pairings.

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed.'));
  });
}

async function database(): Promise<IDBDatabase> {
  const open = indexedDB.open(DATABASE, 1);
  open.onupgradeneeded = () => {
    if (!open.result.objectStoreNames.contains(STORE)) open.result.createObjectStore(STORE, { keyPath: 'pairingId' });
  };
  return request(open);
}

async function withStore<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => Promise<T>): Promise<T> {
  const db = await database();
  try {
    const transaction = db.transaction(STORE, mode);
    const done = new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB transaction aborted.'));
    });
    const result = await run(transaction.objectStore(STORE));
    await done;
    return result;
  } finally {
    db.close();
  }
}

function summary(pairing: StoredPairing): PairingSummary {
  return { pairingId: pairing.pairingId, grants: pairing.grants, createdAt: pairing.createdAt, autoConnect: pairing.autoConnect, ...(pairing.lastConnectedAt ? { lastConnectedAt: pairing.lastConnectedAt } : {}) };
}

function writeHint(pairings: readonly PairingSummary[]): void {
  try {
    if (pairings.some((pairing) => pairing.autoConnect)) window.localStorage.setItem(AUTO_CONNECT_HINT, '1');
    else window.localStorage.removeItem(AUTO_CONNECT_HINT);
  } catch { /* storage unavailable: auto-connect simply stays off */ }
}

export function autoConnectHinted(): boolean {
  try { return window.localStorage.getItem(AUTO_CONNECT_HINT) === '1'; } catch { return false; }
}

export async function loadPairings(): Promise<StoredPairing[]> {
  const all = await withStore('readonly', (store) => request(store.getAll() as IDBRequest<StoredPairing[]>));
  const sorted = all.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const summaries = sorted.map(summary);
  writeHint(summaries);
  updateAgentState({ pairings: summaries, loaded: true });
  return sorted;
}

export async function getPairing(pairingId: string): Promise<StoredPairing | undefined> {
  return withStore('readonly', (store) => request(store.get(pairingId) as IDBRequest<StoredPairing | undefined>));
}

export async function savePairing(pairing: StoredPairing): Promise<void> {
  await withStore('readwrite', (store) => { store.put(pairing); return Promise.resolve(); });
  await loadPairings();
}

export async function deletePairing(pairingId: string): Promise<void> {
  await withStore('readwrite', (store) => { store.delete(pairingId); return Promise.resolve(); });
  await loadPairings();
}

export async function setAutoConnect(pairingId: string, autoConnect: boolean): Promise<void> {
  const pairing = await getPairing(pairingId);
  if (!pairing) return;
  await savePairing({ ...pairing, autoConnect });
}

// ---------------------------------------------------------------------------
// Actions. Each one that may touch the network is a user act; the runtime
// chunk (and with it any WebSocket code) loads only here.


let host: GatewayHost | null = null;
/** The workspace lends its sessions, renderer and checkpoint writer. */
export function registerAgentHost(value: GatewayHost | null): void { host = value; }
export function agentHost(): GatewayHost | null { return host; }

const runtime = () => import('./bridge-runtime');

/** The user clicked Allow on a pairing link. */
export async function allowPairing(link: PairingLink): Promise<void> {
  updateAgentState({ pendingLink: null, linkError: null });
  await (await runtime()).pair(link);
}

export function denyPairing(): void {
  updateAgentState({ pendingLink: null });
}

/** The user clicked Connect (or auto-connect for a pairing the user marked). */
export async function connectPairing(pairingId: string): Promise<void> {
  await (await runtime()).resume(pairingId);
}

export async function disconnectBridge(): Promise<void> {
  if (state.status.state === 'idle') return;
  (await runtime()).disconnect();
}

/** Forget a pairing here, and tell the helper when connected with it. */
export async function revokePairing(pairingId: string): Promise<void> {
  const active = state.status.state === 'connected' || state.status.state === 'connecting' ? state.status.pairingId : undefined;
  if (active === pairingId) (await runtime()).revokeActive();
  await deletePairing(pairingId);
}

export function setFullPreview(sessionId: string, allowed: boolean): void {
  const next = new Set(state.fullPreviewSessions);
  if (allowed) next.add(sessionId); else next.delete(sessionId);
  updateAgentState({ fullPreviewSessions: next });
}

/** Runs once per page load: auto-connect only for a user-granted pairing. */
export async function startAgentAccess(): Promise<void> {
  if (!autoConnectHinted()) return;
  const pairings = await loadPairings();
  const automatic = pairings.find((pairing) => pairing.autoConnect);
  if (automatic) await connectPairing(automatic.pairingId);
}
